import { AlertTypes } from '../data/options';
import { triggerAuthFailure } from '../utils/authFailureHandler';
import { baseURL } from './baseUrl';

export { baseURL };

// ---------------------------------------------------------------------------
// Error extraction
// ---------------------------------------------------------------------------

// Gateway / CDN errors (CloudFront 502/504, nginx) answer with an HTML page.
// That is never a message for the user (bug 416 showed the raw markup in the
// alert); report it as a plain status failure like the axios client did.
const isHtmlBody = (data) =>
    typeof data === 'string' && /^\s*<(!doctype|html)/i.test(data);

const extractErrorDetail = (data) => {
    if (!data) return null;
    if (typeof data === 'string') return isHtmlBody(data) ? null : data;

    const payload =
        data.data && typeof data.data === 'object' && (data.data.detail || data.data.status_code)
            ? data.data
            : data;

    if (typeof payload.detail === 'string') {
        return payload.detail;
    }

    if (Array.isArray(payload.detail)) {
        return payload.detail
            .map((error) => {
                if (typeof error === 'object' && error.msg) return error.msg;
                if (typeof error === 'string') return error;
                return JSON.stringify(error);
            })
            .join('; ');
    }

    if (typeof payload.message === 'string') {
        return payload.message;
    }

    return null;
};

// PostgREST RPCs can answer 200 with an error envelope in the body
// (`{ status_code: 4xx, detail }` or nested under `data`).
const getEmbeddedErrorStatus = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

    if (data.data && typeof data.data === 'object' && data.data.status_code >= 400) {
        return data.data.status_code;
    }

    if (data.status_code >= 400) {
        return data.status_code;
    }

    return null;
};

const isAuthSessionError = (detail) => {
    if (!detail) return true;

    const normalized = detail.toLowerCase();
    if (/not allowed|permission|forbidden|access denied/.test(normalized)) {
        return false;
    }

    return /session|token|expired|credential|authenticated|login|invalid.*auth|missing.*auth/.test(
        normalized
    );
};

// Turns an error payload into a thrown Error. A 401 that looks like a dead
// session logs the user out through the auth-failure handler.
const throwApiError = (data, status, { handleSessionError = true } = {}) => {
    // Never log the response body here: error payloads can carry tokens,
    // emails, or other sensitive details.
    console.log('Response status:', status);

    const detail = extractErrorDetail(data);
    const effectiveStatus = status || getEmbeddedErrorStatus(data);

    if (handleSessionError && effectiveStatus === 401 && isAuthSessionError(detail)) {
        triggerAuthFailure();
        throw new Error('Session expired. Please log in again.');
    }

    if (detail) {
        if (detail.includes('TAB_OUT_OF_BOUNDS')) {
            throw new Error('Wrong document sizing. A4 format accepted only');
        }
        throw new Error(detail);
    }

    if (isHtmlBody(data)) {
        throw new Error(effectiveStatus
            ? `Request failed with status code ${effectiveStatus}`
            : 'Unknown server error');
    }
    if (typeof data === 'string') {
        throw new Error(data);
    } else if (typeof data === 'object') {
        throw new Error(JSON.stringify(data));
    } else {
        throw new Error('Unknown server error');
    }
};

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

const parseBody = (response) => {
    const isJSON = response.headers.get('Content-Type')?.includes('application/json');
    return isJSON ? response.json() : response.text();
};

/**
 * Single HTTP entry point for the app.
 *
 * Never throws: resolves `{ data, status, error: null }` on success and
 * `{ data: null, status, error }` on failure, after raising the alert (when a
 * `setAlert` is supplied) and, for a dead session, triggering the auth-failure
 * handler.
 *
 * @param {string} method              HTTP verb.
 * @param {string} endpoint            Path relative to the API root.
 * @param {Object} [options]
 * @param {*}      [options.body]      JSON-serialised unless it is FormData; omitted when undefined.
 * @param {string} [options.apiName]   'api' (PostgREST, default) or 'fastapi'.
 * @param {Object} options.auth        The `{ cls: Auth }` auth state; supplies the bearer token.
 * @param {boolean} [options.requiresAuth=true] Require and send a bearer token. Use false only for public endpoints.
 * @param {Function} [options.setAlert] Alert setter; skipped when null.
 * @param {'auto'|'blob'} [options.parse] 'auto' picks JSON/text from Content-Type.
 */
// Resolved (never thrown) when a request is attempted with no session token:
// before the session is restored, or after logout while a page is still
// unmounting. Callers that must show data then retry once the token is in
// (see `notReady` on the result); nothing is sent as `Bearer null`.
export const NOT_SIGNED_IN = 'Not signed in';

// One 401 → refresh the session → replay the request. The access token lives
// five minutes and is renewed by Auth's timer; a throttled background tab
// (or a renewal that lost a race) can present an expired token, and that is
// not a reason to throw the user out (bug 432 #10). Only when the refresh
// itself fails is the 401 final and handled as a dead session below.
const retryAfterRefresh = async (auth, url, init) => {
    const refresh = auth?.cls?.refreshSession;
    if (typeof refresh !== 'function') return null;
    const renewed = await refresh.call(auth.cls);
    if (!renewed) return null;
    init.headers.Authorization = `Bearer ${auth.cls.jwtToken}`;
    console.log('Session refreshed; replaying:', url);
    return fetch(url, init);
};

export const request = async (
    method,
    endpoint,
    { body, apiName = 'api', auth, setAlert = null, parse = 'auto', requiresAuth = true } = {}
) => {
    const url = baseURL(apiName, endpoint);
    const token = auth?.cls?.jwtToken;
    if (requiresAuth && !token) {
        console.log(`Skipped ${method} ${endpoint}: no session token`);
        return { data: null, status: null, error: NOT_SIGNED_IN, notReady: true };
    }
    const headers = requiresAuth ? { Authorization: `Bearer ${token}` } : {};
    const init = { method, headers };

    if (body !== undefined) {
        if (body instanceof FormData) {
            init.body = body;
        } else {
            headers['Content-Type'] = 'application/json';
            init.body = JSON.stringify(body);
        }
    }

    let status;
    try {
        console.log(`Calling ${method} endpoint:`, url);
        let response = await fetch(url, init);
        if (requiresAuth && response.status === 401) {
            const data = await parseBody(response);
            const replayed = isAuthSessionError(extractErrorDetail(data))
                ? await retryAfterRefresh(auth, url, init)
                : null;
            if (!replayed) throwApiError(data, 401);
            response = replayed;
        }
        status = response.status;

        if (!response.ok) {
            throwApiError(await parseBody(response), response.status, { handleSessionError: requiresAuth });
        }

        const data = parse === 'blob' ? await response.blob() : await parseBody(response);

        if (parse !== 'blob') {
            const embeddedStatus = getEmbeddedErrorStatus(data);
            if (embeddedStatus) throwApiError(data, embeddedStatus, { handleSessionError: requiresAuth });
        }

        return { data, status, error: null };
    } catch (error) {
        console.log('Request Error:', error.message);
        if (setAlert) {
            setAlert({ type: AlertTypes.ERROR, msg: error.message, visible: true });
        }
        return { data: null, status, error: error.message };
    }
};

// ---------------------------------------------------------------------------
// Verb helpers — thin wrappers keeping the call-site signatures.
// ---------------------------------------------------------------------------

export const fetchApiCall = (endpoint, auth, setAlert, apiName = 'api') =>
    request('GET', endpoint, { auth, setAlert, apiName });

export const fetchBlobApiCall = async (endpoint, auth, setAlert, apiName = 'api') => {
    const result = await request('GET', endpoint, { auth, setAlert, apiName, parse: 'blob' });
    return result.error ? { error: result.error } : { blob: result.data };
};

export const postApiCallAsync = (endpoint, payload, auth, setAlert, apiName = 'api', requiresAuth = true) =>
    request('POST', endpoint, { body: payload, auth, setAlert, apiName, requiresAuth });

export const putApiCallAsync = (endpoint, payload, auth, setAlert, apiName = 'api') =>
    request('PUT', endpoint, { body: payload, auth, setAlert, apiName });

export const patchApiCallAsync = (endpoint, payload, auth, setAlert, apiName = 'api') =>
    request('PATCH', endpoint, { body: payload, auth, setAlert, apiName });

export const deleteApiCallAsync = (endpoint, auth, setAlert, apiName = 'api', payload = null) =>
    request('DELETE', endpoint, { body: payload || undefined, auth, setAlert, apiName });

// ---------------------------------------------------------------------------
// Legacy (axios-era) contract: resolves `{ data, status }` on success and
// `undefined` after a failure (alert already raised). Kept for the call sites
// that still branch on `if (!resp) return`; new code should use the helpers
// above and check `result.error`.
// ---------------------------------------------------------------------------

const toLegacyResult = (result) =>
    result.error ? undefined : { data: result.data, status: result.status };

/** @deprecated use postApiCallAsync */
export const postApiCall = async (setAlert, endpoint, filters, auth, apiName = 'api') =>
    toLegacyResult(await request('POST', endpoint, { body: filters ?? {}, auth, setAlert, apiName }));

/** @deprecated use fetchApiCall */
export const getApiCall = async (setAlert, endpoint, auth, apiName = 'api') =>
    toLegacyResult(await request('GET', endpoint, { auth, setAlert, apiName }));
